import type { TFunction } from 'i18next';

import { formatCentsCompact, formatCentsString } from '@/shared/lib/money';

import type { CustomerStatement, CustomerStatementCondition, CustomerStatementTicket } from './types';

export type TranslateFn = TFunction;

export interface StatementLineView {
  item_code: string;
  item_name: string;
  quantity: string;
  unit_price_label: string;
  total_label: string;
}

export interface StatementTicketView {
  sale_order_id: string;
  ticket_label: string;
  date_label: string;
  pet_name: string;
  status_label: string;
  condition: CustomerStatementCondition;
  condition_label: string;
  subtotal_label: string;
  tax_label: string;
  total_label: string;
  paid_label: string;
  pending_label: string;
  lines: StatementLineView[];
}

export interface StatementSummaryView {
  tickets_count: number;
  liquidados_count: number;
  parciales_count: number;
  pendientes_count: number;
  sin_saldo_count: number;
  subtotal_label: string;
  tax_label: string;
  total_label: string;
  paid_label: string;
  pending_label: string;
  condition_label: string;
}

export interface StatementView {
  customer_name: string;
  customer_phone: string;
  customer_email: string | null;
  generated_at_label: string;
  summary: StatementSummaryView;
  tickets: StatementTicketView[];
}

const TICKET_DATE_FORMAT = new Intl.DateTimeFormat('es-MX', {
  weekday: 'long',
  year: 'numeric',
  month: 'long',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
});

const GENERATED_AT_FORMAT = new Intl.DateTimeFormat('es-MX', {
  year: 'numeric',
  month: 'long',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  second: '2-digit',
});

export function buildStatementView(statement: CustomerStatement, t: TranslateFn): StatementView {
  return {
    customer_name: statement.customer.full_name,
    customer_phone: statement.customer.phone,
    customer_email: statement.customer.email,
    generated_at_label: formatDateOrDash(GENERATED_AT_FORMAT, statement.generated_at),
    summary: {
      tickets_count: statement.summary.tickets_count,
      liquidados_count: statement.summary.liquidados_count,
      parciales_count: statement.summary.parciales_count,
      pendientes_count: statement.summary.pendientes_count,
      sin_saldo_count: statement.summary.sin_saldo_count,
      subtotal_label: formatCentsString(statement.summary.subtotal_cents),
      tax_label: formatCentsString(statement.summary.tax_cents),
      total_label: formatCentsString(statement.summary.total_cents),
      paid_label: formatCentsString(statement.summary.paid_cents),
      pending_label: formatCentsString(statement.summary.pending_cents),
      condition_label: t(`customer_statement.overall_${statement.summary.overall_condition}`),
    },
    tickets: statement.tickets.map((ticket) => buildTicketView(ticket, t)),
  };
}

function buildTicketView(ticket: CustomerStatementTicket, t: TranslateFn): StatementTicketView {
  return {
    sale_order_id: ticket.sale_order_id,
    ticket_label: ticket.ticket_label,
    date_label: formatDateOrDash(TICKET_DATE_FORMAT, ticket.created_at),
    pet_name: ticket.pet_name,
    status_label: t(`doctor_pos.status_${ticket.status}`, ticket.status),
    condition: ticket.condition,
    condition_label: t(`customer_statement.condition_${ticket.condition}`),
    subtotal_label: formatCentsString(ticket.subtotal_cents),
    tax_label: formatCentsString(ticket.tax_cents),
    total_label: formatCentsString(ticket.total_cents),
    paid_label: formatCentsString(ticket.paid_cents),
    pending_label: formatCentsString(ticket.pending_cents),
    lines: ticket.lines.map((line) => ({
      item_code: line.item_code,
      item_name: line.item_name,
      quantity: line.quantity,
      unit_price_label: formatCentsCompact(line.unit_price_cents),
      total_label: formatCentsCompact(line.total_cents),
    })),
  };
}

function formatDateOrDash(formatter: Intl.DateTimeFormat, iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '-';
  return formatter.format(date);
}
